/* eslint-disable camelcase */
import { CareerSiteOpeningV2, CareerSiteSettings } from 'api-clients/monolith';
import {
  getJobHoursDisplayText,
  getJobTypeDisplayText,
  getPeriodDisplayText,
} from 'constants/v2/job-options';
import { BriefcaseIcon } from 'icons/briefcase';
import { CreditCardIcon } from 'icons/credit-card';
import { Clock, FileText, Folder, MapPin } from 'lucide-react';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Separator } from 'components/ui/separator';
import { Skeleton } from 'components/ui/skeleton';
import { useJobApplication } from 'hooks/v2/useJobApplication';

import { MonitorIcon } from '../../../icons/monitor-icon';

interface JobsListProps {
  jobs: CareerSiteOpeningV2[];
  brandSetting?: CareerSiteSettings;
}

export function JobsList({ jobs, brandSetting }: JobsListProps) {
  const intl = useIntl();
  const { handleApplyClick } = useJobApplication();
  const remoteLabel = intl.formatMessage({
    defaultMessage: 'Remote',
    description: 'Display label for remote work location',
  });

  if (!jobs || jobs.length === 0) {
    return null;
  }

  return (
    <>
      {jobs.map(job => (
        <div
          key={job.id}
          className="lg:hover:bg-primary-50 group relative flex flex-col rounded-lg transition-colors hover:cursor-pointer"
          data-testid="job-card"
          onClick={() => handleApplyClick(job.id)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleApplyClick(job.id);
            }
          }}
          role="button"
          tabIndex={0}
        >
          <button
            className="relative flex items-center justify-between"
            type="button"
          >
            <div className="flex w-full items-center justify-between gap-4 py-3 lg:p-3">
              <div className="flex flex-1 flex-col items-start gap-2">
                <h3 className="text-left text-base font-medium leading-6 text-gray-900">
                  {job.title}
                </h3>
                <div className="flex flex-row flex-wrap items-start gap-x-4 gap-y-1 text-left text-sm leading-5 text-gray-600">
                  {(job.location || job.is_remote) && (
                    <div className="flex flex-row items-start gap-1">
                      {job.is_remote ? (
                        <>
                          <MonitorIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
                          <span>{remoteLabel}</span>
                        </>
                      ) : (
                        <>
                          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
                          <span className="text-left">{job.location}</span>
                        </>
                      )}
                    </div>
                  )}
                  {job.pay_rate && (
                    <div className="flex flex-row items-center gap-1">
                      <CreditCardIcon className="h-4 w-4 shrink-0 text-gray-500" />
                      <span>
                        {(() => {
                          const idx = job.pay_rate.lastIndexOf('/');
                          if (idx === -1) return job.pay_rate;
                          return (
                            job.pay_rate.substring(0, idx) +
                            getPeriodDisplayText(
                              job.pay_rate.substring(idx + 1),
                              intl,
                            )
                          );
                        })()}
                      </span>
                    </div>
                  )}
                  {job.job_hours && (
                    <div className="flex flex-row items-center gap-1">
                      <Clock className="h-4 w-4 shrink-0 text-gray-500" />
                      <span>{getJobHoursDisplayText(job.job_hours, intl)}</span>
                    </div>
                  )}
                  {brandSetting?.display_position_filter && job.position && (
                    <div className="flex flex-row items-center gap-1">
                      <BriefcaseIcon className="h-4 w-4 shrink-0 text-gray-500" />
                      <span>{job.position}</span>
                    </div>
                  )}
                  {brandSetting?.display_category_filter && job.category && (
                    <div className="flex flex-row items-center gap-1">
                      <Folder className="h-4 w-4 shrink-0 text-gray-500" />
                      <span>{job.category}</span>
                    </div>
                  )}
                  {brandSetting?.display_job_type_filter && job.job_type && (
                    <div className="flex flex-row items-center gap-1">
                      <FileText className="h-4 w-4 shrink-0 text-gray-500" />
                      <span>{getJobTypeDisplayText(job.job_type, intl)}</span>
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={e => {
                  e.stopPropagation();
                  handleApplyClick(job.id);
                }}
                type="button"
                className="border-primary-600 text-primary-600 hover:bg-primary-50 hover:text-primary-700 flex h-10 shrink-0 items-center rounded-full border px-4 text-sm font-medium leading-6 sm:px-6 sm:text-base"
                data-testid="apply-button"
              >
                <FormattedMessage
                  defaultMessage="Apply"
                  description="Apply to position button label"
                />
              </button>
            </div>
          </button>
          <div className="lg:px-3">
            <Separator className="lg:group-hover:opacity-0" />
          </div>
        </div>
      ))}
    </>
  );
}

JobsList.Skeleton = function JobsListSkeleton() {
  return (
    <div
      className="flex items-center justify-between border-b border-gray-200 py-3"
      data-testid="job-skeleton"
    >
      <div className="flex flex-col gap-2">
        <Skeleton className="h-6 w-48" />
        <div className="flex flex-col items-start gap-1 text-sm leading-5 text-gray-600 sm:flex-row sm:items-center sm:gap-4">
          <Skeleton className="h-5 w-36" />
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-5 w-20" />
        </div>
      </div>
      <Skeleton className="h-10 w-20 rounded-full sm:w-28" />
    </div>
  );
};

JobsList.SkeletonGroup = function JobsListSkeletonGroup() {
  return (
    <>
      {Array.from({ length: 10 }, (_, i) => (
        <JobsList.Skeleton key={`skeleton-${i}`} />
      ))}
    </>
  );
};
